import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.concat.js";
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { COLLAPSED_NAVBAR_HEIGHT } from 'styles/globals';
import { pxToRem } from 'utils/ui/units';
import { MINDSET_SIDEBAR_ID, addMindsetSidebarIfRequired, useShowMindsetInPage, useExtraSituationalAwarenessContext, useMindsetLocationContext, useMindsetPageTools, isMindsetSidebarHidden, MINDSET_HIDE_SIDEBAR_EVENT, MINDSET_SHOW_SIDEBAR_EVENT } from 'utils/mindset';
import { getCoachAiName } from 'utils/i18n';
import { COACH_AI_ROUTE } from 'utils/routes';
import useBreakpoints from 'hooks/useBreakpoints';
import CenterContent from 'components/CenterContent';
import Spinner from 'components/Spinner';
import Button from 'components/Button';
import CoachAiAvatar from 'components/Avatar/CoachAIAvatar';
import TooltipButton from 'components/TooltipButton';
import Flex, { Box } from 'components/Flex';
import Text from 'components/Text';
import { useOpenMindsetAgent } from 'utils/mindset/useOpenMindsetAgent';
import { useOpenMindsetWithMessageEvent } from 'utils/mindset/useOpenMindsetWithMessageEvent';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var NAV_HEIGHT = pxToRem(COLLAPSED_NAVBAR_HEIGHT);
var SIDEBAR_WIDTH_PX = 330;
var INSET_PX = 20;
var PANEL_HEADER_HEIGHT = pxToRem(56);

// Reserves layout space in ContentRow so MainContent is pushed left on desktop.
// Separate from SidebarFixed so their visibility can be controlled independently.
var SidebarPlaceholder = styled.div.withConfig({
  displayName: "MindsetSidebar__SidebarPlaceholder",
  componentId: "sc-50xptr-0"
})(["width:", ";flex-shrink:0;"], pxToRem(SIDEBAR_WIDTH_PX));
var AgentContainer = styled.div.withConfig({
  displayName: "MindsetSidebar__AgentContainer",
  componentId: "sc-50xptr-1"
})(["flex:1;min-height:0;overflow:hidden;position:relative;"]);
export function MindsetSidebar() {
  var theme = useTheme();
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var isMindsetEnabled = useShowMindsetInPage();
  var _useState = useState(function () {
      return isMindsetSidebarHidden();
    }),
    _useState2 = _slicedToArray(_useState, 2),
    isHiddenByPage = _useState2[0],
    setIsHiddenByPage = _useState2[1];
  var _useBreakpoints = useBreakpoints('m'),
    isDesktop = _useBreakpoints.m;
  var extraContext = useExtraSituationalAwarenessContext();
  var _useLocation = useLocation(),
    pathname = _useLocation.pathname;
  var isOnCoachAiPage = !!matchPath({
    path: COACH_AI_ROUTE,
    end: false
  }, pathname);
  useLayoutEffect(function () {
    setIsHiddenByPage(isMindsetSidebarHidden());
    function onHide() {
      setIsHiddenByPage(true);
    }
    function onShow() {
      setIsHiddenByPage(false);
    }
    window.addEventListener(MINDSET_HIDE_SIDEBAR_EVENT, onHide);
    window.addEventListener(MINDSET_SHOW_SIDEBAR_EVENT, onShow);
    return function () {
      window.removeEventListener(MINDSET_HIDE_SIDEBAR_EVENT, onHide);
      window.removeEventListener(MINDSET_SHOW_SIDEBAR_EVENT, onShow);
    };
  }, []);
  var _useOpenMindsetAgent = useOpenMindsetAgent(MINDSET_SIDEBAR_ID, addMindsetSidebarIfRequired),
    loadingStatus = _useOpenMindsetAgent.loadingStatus,
    setLoadingStatus = _useOpenMindsetAgent.setLoadingStatus,
    isOpen = _useOpenMindsetAgent.isOpen,
    setIsOpen = _useOpenMindsetAgent.setIsOpen,
    hasInitialized = _useOpenMindsetAgent.hasInitialized,
    setHasInitialized = _useOpenMindsetAgent.setHasInitialized,
    handleOpen = _useOpenMindsetAgent.handleOpen;
  useMindsetLocationContext(hasInitialized, MINDSET_SIDEBAR_ID, extraContext);
  var _useMindsetPageTools = useMindsetPageTools(hasInitialized, MINDSET_SIDEBAR_ID),
    modals = _useMindsetPageTools.modals;
  useOpenMindsetWithMessageEvent(handleOpen);
  useEffect(function () {
    if (!isMindsetEnabled || isOnCoachAiPage || isHiddenByPage || hasInitialized) return;
    // On mobile, delay init until the FAB is tapped so the container is in the DOM
    if (!isDesktop && !isOpen) return;
    addMindsetSidebarIfRequired().then(function () {
      setHasInitialized(true);
      setLoadingStatus('ready');
    }).catch(function () {
      setLoadingStatus('error');
    });
  }, [isMindsetEnabled, isOnCoachAiPage, isHiddenByPage, hasInitialized, setHasInitialized, isDesktop, isOpen, setLoadingStatus]);
  if (!isMindsetEnabled) {
    return null;
  }
  var shouldHide = isOnCoachAiPage || isHiddenByPage;
  var coachAiName = getCoachAiName();
  // Keep panel in DOM after first init to preserve the mounted agent
  var isPanelInDom = isDesktop || isOpen || hasInitialized;
  var isPanelVisible = !shouldHide && (isDesktop || isOpen);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [modals, /*#__PURE__*/_jsx(SidebarPlaceholder, {
      "aria-hidden": "true",
      style: !isDesktop || shouldHide ? {
        display: 'none'
      } : undefined
    }), isPanelInDom && /*#__PURE__*/_jsxs(Box, {
      breakpoints: "m",
      position: "fixed",
      overflow: "hidden",
      display: isPanelVisible ? 'flex' : 'none',
      z: ['modals', 'raised'],
      flexDirection: "column",
      right: 0,
      top: [0, NAV_HEIGHT],
      left: [0, 'unset'],
      bottom: [0, 'unset'],
      width: [undefined, pxToRem(SIDEBAR_WIDTH_PX)],
      height: [undefined, "calc(100vh - ".concat(NAV_HEIGHT, " - (").concat(theme.globals.spacing.s, " * 2))")],
      m: "s",
      borderRadius: "large",
      border: ['none', 'thin'],
      backgroundColor: "background",
      boxShadow: ['raised', 'none'],
      children: [!isDesktop && /*#__PURE__*/_jsxs(Flex, {
        gap: "xs",
        px: "s",
        height: PANEL_HEADER_HEIGHT,
        shrink: 0,
        borderBottom: "thin",
        children: [/*#__PURE__*/_jsx(CoachAiAvatar, {}), /*#__PURE__*/_jsx(Box, {
          grow: 1,
          children: /*#__PURE__*/_jsx(Text, {
            fontWeight: "bold",
            children: coachAiName
          })
        }), /*#__PURE__*/_jsx(Button, {
          iconOnly: true,
          icon: "x",
          variant: "transparent",
          onClick: function onClick() {
            return setIsOpen(false);
          },
          children: t('actions_close')
        })]
      }), /*#__PURE__*/_jsx(AgentContainer, {
        id: MINDSET_SIDEBAR_ID,
        children: loadingStatus === 'loading' && /*#__PURE__*/_jsx(CenterContent, {
          fillParent: true,
          children: /*#__PURE__*/_jsx(Spinner, {})
        })
      })]
    }), !isDesktop && !isOpen && !shouldHide && /*#__PURE__*/_jsx(TooltipButton, {
      round: true,
      iconOnly: true,
      variant: "transparent",
      icon: /*#__PURE__*/_jsx(Box, {
        mr: "-xs",
        ml: "-xs",
        children: /*#__PURE__*/_jsx(CoachAiAvatar, {
          scale: 1.5
        })
      }),
      position: "fixed",
      z: "alerts",
      tooltipPlacement: "top-end",
      onClick: function onClick() {
        return setIsOpen(true);
      },
      style: {
        right: INSET_PX,
        bottom: INSET_PX
      },
      children: t('coach_ai_ask', {
        coachAiName: coachAiName
      })
    })]
  });
}